import * as d3 from 'd3';
import React, {useEffect, useId, useMemo, useRef} from 'react';
import {useQuery, useQueryClient} from 'react-query';

import {
  ObsInspectResult,
  SetupSectionProps,
  SiteCleanupParams,
  SiteObsUploadConfig,
  SiteObservationsPayload,
  getStateViewDefaults,
  setupWizardApi,
} from 'src/lib/api/setupWizard';
import {
  ARE_OBSERVATIONS_AVAILABLE_COLUMN,
  GAUGE_ID_COLUMN,
  GAUGE_TYPE_COLUMN,
  OBS_DATA_FREQUENCY_OPTIONS,
  OBS_DATA_SOURCE_TYPE_OPTIONS,
  OBS_DATA_UNITS_OPTIONS,
  OBS_FLOW_TYPE_OPTIONS,
  OBS_SAMPLE_TIMING_OPTIONS,
  SITE_NAME_COLUMN,
} from 'src/lib/setupWizardConstants';
import {buttonPrimary} from 'src/lib/tailwindClasses';

import {ApiErrorMessage} from './ApiErrorMessage';
import {CliCommandBlock} from './CliCommandBlock';
import {PrerequisiteWarning, SectionErrors} from './SetupStepSection';
import {useSetupMutation} from './useSetupMutation';

function makeSiteObsQueryKey(orgId: string, projectId: string, siteName: string) {
  return ['setupWizard', 'siteObs', orgId, projectId, siteName];
}

// --- Timezone datalist ---

const COMMON_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Boise',
  'America/Phoenix',
  'America/Anchorage',
  'America/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'America/Edmonton',
  'America/Sao_Paulo',
  'America/Mexico_City',
  'America/Lima',
  'America/Bogota',
  'America/Santiago',
  'America/Argentina/Buenos_Aires',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Rome',
  'Europe/Madrid',
  'Europe/Amsterdam',
  'Europe/Zurich',
  'Europe/Stockholm',
  'Europe/Helsinki',
  'Europe/Warsaw',
  'Europe/Istanbul',
  'Europe/Moscow',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Perth',
  'Pacific/Auckland',
  'Pacific/Honolulu',
  'US/Eastern',
  'US/Central',
  'US/Mountain',
  'US/Pacific',
  'US/Alaska',
];

// --- Chart helpers ---

interface ParsedObs {
  date: Date;
  value: number;
}

interface ChartCleanupParams {
  maskAbove: number | null;
  maskBelow: number | null;
  sigma: number;
}

interface GapInterval {
  start: Date;
  end: Date;
}

function parseRecords(
  records: Array<Record<string, number | null | string>> | null
): {data: ParsedObs[]; col: string; gaps: GapInterval[]} | null {
  if (!records || records.length === 0) return null;
  const col = Object.keys(records[0]).find((k) => k !== 'date');
  if (!col) return null;

  const data: ParsedObs[] = [];
  const gaps: GapInterval[] = [];
  let gapStart: Date | null = null;

  for (const r of records) {
    const v = r[col] as number | null;
    const d = r['date'] ? new Date(r['date'] as string) : null;
    if (!d) continue;
    if (v !== null && !isNaN(v as number)) {
      if (gapStart !== null) {
        gaps.push({start: gapStart, end: d});
        gapStart = null;
      }
      data.push({date: d, value: v as number});
    } else {
      if (gapStart === null) gapStart = d;
    }
  }
  if (gapStart !== null && records.length > 0) {
    const last = records[records.length - 1];
    if (last['date']) gaps.push({start: gapStart, end: new Date(last['date'] as string)});
  }

  return data.length > 0 ? {data, col, gaps} : null;
}

function buildGaussianKernel(sigma: number): number[] {
  const radius = Math.ceil(sigma * 3);
  const raw = Array.from({length: 2 * radius + 1}, (_, i) => {
    const x = i - radius;
    return Math.exp((-x * x) / (2 * sigma * sigma));
  });
  const sum = raw.reduce((a, b) => a + b, 0);
  return raw.map((w) => w / sum);
}

function gaussianSmooth(values: number[], kernel: number[]): number[] {
  const radius = (kernel.length - 1) / 2;
  return values.map((_, i) => {
    let sum = 0;
    let wsum = 0;
    for (let k = 0; k < kernel.length; k++) {
      const j = i + k - radius;
      if (j >= 0 && j < values.length) {
        sum += values[j] * kernel[k];
        wsum += kernel[k];
      }
    }
    return sum / wsum;
  });
}

function applyCleanup(data: ParsedObs[], params: ChartCleanupParams): ParsedObs[] {
  const cleaned = data.filter(
    (d) =>
      (params.maskAbove === null || d.value <= params.maskAbove) &&
      (params.maskBelow === null || d.value >= params.maskBelow)
  );

  if (params.sigma > 0 && cleaned.length > 1) {
    const kernel = buildGaussianKernel(params.sigma);
    const smoothed = gaussianSmooth(
      cleaned.map((d) => d.value),
      kernel
    );
    return cleaned.map((d, i) => ({...d, value: smoothed[i]}));
  }

  return cleaned;
}

// --- Interactive chart with d3-zoom ---

const CHART_WIDTH = 700;
const OBS_HEIGHT = 160;
const EXTRA_PANEL_HEIGHT = 80;
const GAP = 4;
const MARGIN = {top: 12, right: 16, bottom: 32, left: 56};

interface ExtraPanel {
  key: string;
  data: ParsedObs[];
  label: string;
  color: string;
  legendLabel: string;
  legendColorClass: string;
  invertY: boolean;
}

function ObsChart({
  obsData,
  cleanupParams,
}: {
  obsData: SiteObservationsPayload;
  cleanupParams: ChartCleanupParams;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const baseClipId = `obs-clip-${useId().replace(/\W/g, '')}`;

  const parsed = useMemo(() => parseRecords(obsData.obs), [obsData]);

  const extraPanels = useMemo((): ExtraPanel[] => {
    const panels: ExtraPanel[] = [];
    const precipParsed = parseRecords(obsData.precipitation);
    if (precipParsed && precipParsed.data.length > 0) {
      panels.push({
        key: 'precip',
        data: precipParsed.data,
        label: 'mm/h',
        color: '#38bdf8',
        legendLabel: 'precip',
        legendColorClass: 'bg-sky-400',
        invertY: true,
      });
    }
    const tempParsed = parseRecords(obsData.temperature);
    if (tempParsed && tempParsed.data.length > 0) {
      panels.push({
        key: 'temp',
        data: tempParsed.data,
        label: '°C',
        color: '#f97316',
        legendLabel: 'temp',
        legendColorClass: 'bg-orange-500',
        invertY: false,
      });
    }
    return panels;
  }, [obsData]);

  const innerW = CHART_WIDTH - MARGIN.left - MARGIN.right;
  const hasExtra = extraPanels.length > 0;
  const totalHeight = OBS_HEIGHT + extraPanels.length * (GAP + EXTRA_PANEL_HEIGHT);
  const obsInnerH = (hasExtra ? OBS_HEIGHT : totalHeight - MARGIN.bottom) - MARGIN.top - GAP;

  const cleaned = useMemo(
    () => (parsed ? applyCleanup(parsed.data, cleanupParams) : []),
    [parsed, cleanupParams]
  );

  const drawRef = useRef<(transform: d3.ZoomTransform) => void>(() => {});
  useEffect(() => {
    drawRef.current = (transform: d3.ZoomTransform) => {
      if (!svgRef.current || !parsed) return;

      const svg = d3.select(svgRef.current);

      const xBase = d3
        .scaleTime()
        .domain(d3.extent(parsed.data, (d) => d.date) as [Date, Date])
        .range([0, innerW]);
      const x = transform.rescaleX(xBase);

      const yObs = d3
        .scaleLinear()
        .domain(d3.extent(parsed.data, (d) => d.value) as [number, number])
        .nice()
        .range([obsInnerH, 0]);

      const line = d3
        .line<ParsedObs>()
        .x((d) => x(d.date))
        .y((d) => yObs(d.value));

      svg.select<SVGPathElement>('.raw-line').datum(parsed.data).attr('d', line);
      svg.select<SVGPathElement>('.clean-line').datum(cleaned).attr('d', line);
      svg
        .select<SVGGElement>('.obs-x-axis')
        .call(
          hasExtra
            ? d3.axisBottom(x).ticks(0).tickSizeOuter(0)
            : d3.axisBottom(x).ticks(6).tickSizeOuter(0)
        );
      svg.select<SVGGElement>('.obs-y-axis').call(d3.axisLeft(yObs).ticks(5).tickSizeOuter(0));

      const obsG = svg.select<SVGGElement>('.obs-area');

      const gapsG = svg.select<SVGGElement>('.gaps-group');
      gapsG.selectAll('rect').remove();
      for (const gap of parsed.gaps) {
        const x1 = x(gap.start);
        const x2 = x(gap.end);
        if (x2 > 0 && x1 < innerW) {
          gapsG
            .append('rect')
            .attr('x', x1)
            .attr('y', 0)
            .attr('width', Math.max(0, x2 - x1))
            .attr('height', obsInnerH)
            .attr('fill', '#d1d5db')
            .attr('opacity', 0.6);
        }
      }

      obsG.selectAll('.mask-line').remove();
      for (const threshold of [cleanupParams.maskAbove, cleanupParams.maskBelow]) {
        if (threshold !== null) {
          obsG
            .append('line')
            .attr('class', 'mask-line')
            .attr('x1', 0)
            .attr('x2', innerW)
            .attr('y1', yObs(threshold))
            .attr('y2', yObs(threshold))
            .attr('stroke', '#ef4444')
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', '4,3');
        }
      }

      extraPanels.forEach((panel, i) => {
        const isLast = i === extraPanels.length - 1;
        const panelH = EXTRA_PANEL_HEIGHT - (isLast ? MARGIN.bottom : 0);
        const domain = panel.invertY
          ? [d3.max(panel.data, (d) => d.value) ?? 1, 0]
          : (d3.extent(panel.data, (d) => d.value) as [number, number]);
        const range: [number, number] = panel.invertY ? [0, panelH] : [panelH, 0];

        const y = d3.scaleLinear().domain(domain).nice().range(range);
        const panelLine = d3
          .line<ParsedObs>()
          .x((d) => x(d.date))
          .y((d) => y(d.value));

        svg.select<SVGPathElement>(`.${panel.key}-line`).datum(panel.data).attr('d', panelLine);
        svg
          .select<SVGGElement>(`.${panel.key}-x-axis`)
          .call(
            isLast
              ? d3.axisBottom(x).ticks(6).tickSizeOuter(0)
              : d3.axisBottom(x).ticks(0).tickSizeOuter(0)
          );
        svg
          .select<SVGGElement>(`.${panel.key}-y-axis`)
          .call(d3.axisLeft(y).ticks(3).tickSizeOuter(0));
      });
    };
  }, [parsed, cleaned, cleanupParams, extraPanels, hasExtra, innerW, obsInnerH]);

  useEffect(() => {
    if (!svgRef.current || !parsed) return;

    const svg = d3.select(svgRef.current);

    svg.selectAll('*').remove();

    const defs = svg.append('defs');
    defs
      .append('clipPath')
      .attr('id', baseClipId)
      .append('rect')
      .attr('width', innerW)
      .attr('height', obsInnerH);

    const obsG = svg
      .append('g')
      .attr('class', 'obs-area')
      .attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    obsG.append('g').attr('class', 'obs-x-axis').attr('transform', `translate(0,${obsInnerH})`);
    obsG.append('g').attr('class', 'obs-y-axis');
    obsG
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -obsInnerH / 2)
      .attr('y', -42)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .text(parsed.col);

    const obsClipped = obsG.append('g').attr('clip-path', `url(#${baseClipId})`);
    obsClipped.append('g').attr('class', 'gaps-group');
    obsClipped
      .append('path')
      .attr('class', 'raw-line')
      .attr('fill', 'none')
      .attr('stroke', '#93c5fd')
      .attr('stroke-width', 1);
    obsClipped
      .append('path')
      .attr('class', 'clean-line')
      .attr('fill', 'none')
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 1.5);

    let yOffset = MARGIN.top + obsInnerH + GAP;
    extraPanels.forEach((panel, i) => {
      const isLast = i === extraPanels.length - 1;
      const panelH = EXTRA_PANEL_HEIGHT - (isLast ? MARGIN.bottom : 0);
      const panelClipId = `${baseClipId}-${panel.key}`;

      defs
        .append('clipPath')
        .attr('id', panelClipId)
        .append('rect')
        .attr('width', innerW)
        .attr('height', panelH);

      const g = svg
        .append('g')
        .attr('class', `${panel.key}-panel`)
        .attr('transform', `translate(${MARGIN.left},${yOffset})`);

      g.append('g')
        .attr('class', `${panel.key}-x-axis`)
        .attr('transform', `translate(0,${panelH})`);
      g.append('g').attr('class', `${panel.key}-y-axis`);
      g.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('x', -panelH / 2)
        .attr('y', -42)
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .attr('fill', '#6b7280')
        .text(panel.label);

      g.append('g')
        .attr('clip-path', `url(#${panelClipId})`)
        .append('path')
        .attr('class', `${panel.key}-line`)
        .attr('fill', 'none')
        .attr('stroke', panel.color)
        .attr('stroke-width', 1);

      yOffset += panelH + GAP;
    });

    svg
      .append('rect')
      .attr('class', 'zoom-rect')
      .attr('x', MARGIN.left)
      .attr('y', MARGIN.top)
      .attr('width', innerW)
      .attr('height', totalHeight - MARGIN.top - MARGIN.bottom)
      .attr('fill', 'none')
      .attr('pointer-events', 'all');

    const zoomRect = svg.select<SVGRectElement>('.zoom-rect');
    const zoom = d3
      .zoom<SVGRectElement, unknown>()
      .scaleExtent([1, 50])
      .translateExtent([
        [0, 0],
        [innerW, totalHeight],
      ])
      .extent([
        [0, 0],
        [innerW, totalHeight],
      ])
      .on('zoom', (event: d3.D3ZoomEvent<SVGRectElement, unknown>) => {
        drawRef.current(event.transform);
      });

    zoomRect.call(zoom);
    drawRef.current(d3.zoomIdentity);
  }, [parsed, extraPanels]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!svgRef.current || !parsed) return;
    const svg = d3.select(svgRef.current);
    const zoomRect = svg.select<SVGRectElement>('.zoom-rect');
    if (zoomRect.empty()) return;

    const currentTransform = d3.zoomTransform(zoomRect.node()!);
    drawRef.current(currentTransform);
  }, [cleanupParams, cleaned, parsed]);

  if (!parsed) {
    return <p className="text-sm text-gray-500">No observation data available.</p>;
  }

  return (
    <div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${CHART_WIDTH} ${totalHeight}`}
        className="w-full bg-white border border-gray-100 rounded"
        style={{maxHeight: totalHeight * 1.2, cursor: 'grab'}}
      />
      <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="inline-block w-4 h-0.5 bg-blue-300" /> raw
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-4 h-0.5 bg-blue-600" /> cleaned
        </span>
        {parsed.gaps.length > 0 && (
          <span className="flex items-center gap-1">
            <span className="inline-block w-4 h-3 bg-gray-300 opacity-60" /> gap
          </span>
        )}
        {(cleanupParams.maskAbove !== null || cleanupParams.maskBelow !== null) && (
          <span className="flex items-center gap-1">
            <span className="inline-block w-4 h-0 border-t border-dashed border-red-500" /> mask
            threshold
          </span>
        )}
        {extraPanels.map((panel) => (
          <span key={panel.key} className="flex items-center gap-1">
            <span className={`inline-block w-4 h-0.5 ${panel.legendColorClass}`} />{' '}
            {panel.legendLabel}
          </span>
        ))}
        <span className="ml-auto">Scroll to zoom, drag to pan</span>
      </div>
    </div>
  );
}

// --- Public gauge chart preview ---

const NULL_CLEANUP: ChartCleanupParams = {maskAbove: null, maskBelow: null, sigma: 0};

function GaugeDataChart({
  orgId,
  projectId,
  siteName,
}: {
  orgId: string;
  projectId: string;
  siteName: string;
}) {
  const [show, setShow] = React.useState(false);
  const [selectedCol, setSelectedCol] = React.useState<string | null>(null);

  const {data, isLoading, isError} = useQuery(
    makeSiteObsQueryKey(orgId, projectId, siteName),
    () => setupWizardApi.getSiteObservations(orgId, projectId, siteName),
    {enabled: show}
  );

  const availableCols = React.useMemo(() => {
    if (!data?.obs?.[0]) return [];
    return Object.keys(data.obs[0]).filter((k) => k !== 'date');
  }, [data]);

  const activeCol = selectedCol ?? availableCols[0] ?? null;

  const obsData = React.useMemo(() => {
    if (!data || !activeCol) return data;
    return {
      ...data,
      obs:
        data.obs?.map((r) => ({date: r['date'], [activeCol]: r[activeCol]})) ??
        null,
    };
  }, [data, activeCol]);

  return (
    <div className="mt-2">
      <button
        type="button"
        className="text-xs text-blue-600 hover:underline"
        onClick={() => setShow((v) => !v)}
      >
        {show ? 'Hide chart' : 'Show chart'}
      </button>
      {show && (
        <div className="mt-2">
          {isLoading ? (
            <p className="text-xs text-gray-500">Loading…</p>
          ) : isError ? (
            <p className="text-xs text-red-500">Failed to load chart data.</p>
          ) : obsData ? (
            <>
              {availableCols.length > 1 && (
                <select
                  className="mb-2 text-xs border rounded px-1 py-0.5"
                  value={activeCol ?? ''}
                  onChange={(e) => setSelectedCol(e.target.value)}
                >
                  {availableCols.map((col) => (
                    <option key={col} value={col}>
                      {col}
                    </option>
                  ))}
                </select>
              )}
              <ObsChart obsData={obsData} cleanupParams={NULL_CLEANUP} />
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

// --- Per-site file uploader with inspect-driven inline config ---

const DATE_LIKE_NAMES = new Set(['date', 'datetime', 'time', 'timestamp', 'dt']);
const FLOW_LIKE_NAMES = new Set(['flow', 'discharge', 'inflow', 'outflow', 'stage', 'cfs', 'cms']);

function autoDetect(columns: string[], hints: Set<string>): string {
  const idx = columns.findIndex((c) => hints.has(c.toLowerCase()));
  return idx >= 0 ? columns[idx] : '';
}

const TZ_DATALIST_ID = 'obs-tz-datalist';

interface SiteObsUploaderProps {
  orgId: string;
  projectId: string;
  siteName: string;
  uploadedSummary: ReturnType<typeof getStateViewDefaults>['uploadedObsSummary'][number] | undefined;
  queryKey: readonly ['setupWizard', 'project', string, string];
  cleanupParams: SiteCleanupParams;
  onSaveCleanup: (params: SiteCleanupParams) => Promise<void>;
  isMutating: boolean;
}

function SiteObsUploader({
  orgId,
  projectId,
  siteName,
  uploadedSummary,
  queryKey,
  cleanupParams,
  onSaveCleanup,
  isMutating,
}: SiteObsUploaderProps) {
  const queryClient = useQueryClient();

  const [clearKey, setClearKey] = React.useState(0);
  const [file, setFile] = React.useState<File | null>(null);
  const [inspectResult, setInspectResult] = React.useState<ObsInspectResult | null>(null);
  const [isInspecting, setIsInspecting] = React.useState(false);
  const [inspectError, setInspectError] = React.useState<string | null>(null);
  const [collapsed, setCollapsed] = React.useState(false);
  const [uploadSuccess, setUploadSuccess] = React.useState(false);

  // Config
  const [sheetName, setSheetName] = React.useState('');
  const [datetimeCol, setDatetimeCol] = React.useState('');
  const [obsCol, setObsCol] = React.useState('');
  const [timezone, setTimezone] = React.useState('UTC');
  const [dataFrequency, setDataFrequency] = React.useState<string>('Daily');
  const [dataUnits, setDataUnits] = React.useState<string>('CFS');
  const [sampleTiming, setSampleTiming] = React.useState<string>('Start of Period');
  const [typeOfFlow, setTypeOfFlow] = React.useState<string>('flow');
  const [dataSourceType, setDataSourceType] = React.useState<string>('Private');
  const [missingDataValue, setMissingDataValue] = React.useState('-9999');

  // Advanced
  const [headerRow, setHeaderRow] = React.useState('0');
  const [dayfirst, setDayfirst] = React.useState(false);
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const [showPreview, setShowPreview] = React.useState(false);

  // Preview chart
  const [previewObs, setPreviewObs] = React.useState<SiteObservationsPayload | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = React.useState(false);
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  const previewTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup params (merged from CleanupParamsForm)
  const [maskAbove, setMaskAbove] = React.useState(String(cleanupParams['mask_above'] ?? ''));
  const [maskBelow, setMaskBelow] = React.useState(String(cleanupParams['mask_below'] ?? ''));
  const [gaussianSigma, setGaussianSigma] = React.useState(
    String(cleanupParams['discharge_gaussian_smooth_sigma'] ?? '0')
  );
  const [isSavingCleanup, setIsSavingCleanup] = React.useState(false);

  React.useEffect(() => {
    setMaskAbove(String(cleanupParams['mask_above'] ?? ''));
    setMaskBelow(String(cleanupParams['mask_below'] ?? ''));
    setGaussianSigma(String(cleanupParams['discharge_gaussian_smooth_sigma'] ?? '0'));
  }, [cleanupParams]);

  // Fetch saved obs (used as chart source when no preview is active)
  const {data: savedObsData} = useQuery(
    makeSiteObsQueryKey(orgId, projectId, siteName),
    () => setupWizardApi.getSiteObservations(orgId, projectId, siteName),
    {enabled: uploadedSummary?.hasData ?? false}
  );

  const chartData = previewObs ?? savedObsData ?? null;

  const liveCleanupParams = useMemo(
    (): ChartCleanupParams => ({
      maskAbove: maskAbove === '' ? null : Number(maskAbove),
      maskBelow: maskBelow === '' ? null : Number(maskBelow),
      sigma: Number(gaussianSigma) || 0,
    }),
    [maskAbove, maskBelow, gaussianSigma]
  );

  const hasCleanupChanges =
    maskAbove !== String(cleanupParams['mask_above'] ?? '') ||
    maskBelow !== String(cleanupParams['mask_below'] ?? '') ||
    gaussianSigma !== String(cleanupParams['discharge_gaussian_smooth_sigma'] ?? '0');

  const handleSaveCleanup = async () => {
    setIsSavingCleanup(true);
    try {
      await onSaveCleanup({
        mask_above: liveCleanupParams.maskAbove,
        mask_below: liveCleanupParams.maskBelow,
        discharge_gaussian_smooth_sigma: liveCleanupParams.sigma,
      });
    } finally {
      setIsSavingCleanup(false);
    }
  };

  // Upload
  const [isUploading, setIsUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);

  // True when the inspected file was XLSX (sheets is non-null); fall back to file extension/type
  // when inspect hasn't run yet (e.g. during the initial inspect call).
  const isXlsx = inspectResult
    ? inspectResult.sheets !== null
    : !!file &&
      (file.name.toLowerCase().endsWith('.xlsx') ||
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

  const runInspect = React.useCallback(
    async (f: File, opts?: {sheetName?: string; header?: number}) => {
      setIsInspecting(true);
      setInspectError(null);
      try {
        const result = await setupWizardApi.inspectObsFile(orgId, projectId, f, opts);
        setInspectResult(result);
        // Auto-detect columns on first inspect
        if (!opts?.sheetName) {
          setDatetimeCol(autoDetect(result.columns, DATE_LIKE_NAMES));
          setObsCol(autoDetect(result.columns, FLOW_LIKE_NAMES));
        } else {
          // Re-inspect due to sheet change: reset column picks
          setDatetimeCol(autoDetect(result.columns, DATE_LIKE_NAMES));
          setObsCol(autoDetect(result.columns, FLOW_LIKE_NAMES));
        }
      } catch (e) {
        setInspectError(e instanceof Error ? e.message : 'Failed to read file');
        setInspectResult(null);
      } finally {
        setIsInspecting(false);
      }
    },
    [orgId, projectId]
  );

  // Auto-preview: re-runs 500 ms after any config change (debounced)
  React.useEffect(() => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    if (!file || !datetimeCol || !obsCol || !timezone) {
      setPreviewObs(null);
      setIsPreviewLoading(false);
      return;
    }
    previewTimerRef.current = setTimeout(async () => {
      setIsPreviewLoading(true);
      setPreviewError(null);
      try {
        const config: SiteObsUploadConfig = {
          observation_column_name: obsCol,
          datetime_column_name: datetimeCol,
          timezone_of_observations: timezone,
          data_frequency: dataFrequency,
          data_units: dataUnits,
          sample_timing: sampleTiming,
          type_of_flow: typeOfFlow,
          data_source_type: dataSourceType,
          missing_data_value: missingDataValue,
          ...(sheetName ? {sheet_name: sheetName} : {}),
          ...(parseInt(headerRow) > 0 ? {header: parseInt(headerRow)} : {}),
          ...(dayfirst ? {dayfirst: true} : {}),
        };
        const result = await setupWizardApi.previewObservations(orgId, projectId, siteName, file, config);
        setPreviewObs(result ?? null);
      } catch (e) {
        setPreviewError(e instanceof Error ? e.message : 'Preview failed');
        setPreviewObs(null);
      } finally {
        setIsPreviewLoading(false);
      }
    }, 500);
    return () => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    };
  }, [file, datetimeCol, obsCol, timezone, dataFrequency, dataUnits, sampleTiming, typeOfFlow, missingDataValue, sheetName, headerRow, dayfirst, orgId, projectId, siteName]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileSelect = (newFile: File | null) => {
    setFile(newFile);
    setInspectResult(null);
    setInspectError(null);
    setShowPreview(false);
    setSheetName('');
    setDatetimeCol('');
    setObsCol('');
    setUploadError(null);
    setUploadSuccess(false);
    if (newFile) runInspect(newFile);
  };

  const handleSheetChange = (newSheet: string) => {
    setSheetName(newSheet);
    if (file) runInspect(file, {sheetName: newSheet, header: parseInt(headerRow) || 0});
  };

  const handleHeaderChange = (val: string) => {
    setHeaderRow(val);
    const h = parseInt(val);
    if (!isNaN(h) && file) {
      runInspect(file, {sheetName: sheetName || undefined, header: h});
    }
  };

  const canUpload =
    !!file && !!datetimeCol && !!obsCol && !!timezone && !isInspecting && !isUploading;

  const handleUpload = async () => {
    if (!file || !canUpload) return;
    setIsUploading(true);
    setUploadError(null);
    try {
      const config: SiteObsUploadConfig = {
        observation_column_name: obsCol,
        datetime_column_name: datetimeCol,
        timezone_of_observations: timezone,
        data_frequency: dataFrequency,
        data_units: dataUnits,
        sample_timing: sampleTiming,
        type_of_flow: typeOfFlow,
        data_source_type: dataSourceType,
        missing_data_value: missingDataValue,
        ...(sheetName ? {sheet_name: sheetName} : {}),
        ...(parseInt(headerRow) > 0 ? {header: parseInt(headerRow)} : {}),
        ...(dayfirst ? {dayfirst: true} : {}),
      };
      const result = await setupWizardApi.uploadObservations(orgId, projectId, siteName, file, config);
      queryClient.setQueryData(queryKey, result);
      // The saved-series query feeds the chart below.
      queryClient.invalidateQueries(makeSiteObsQueryKey(orgId, projectId, siteName));
      setFile(null);
      setClearKey((k) => k + 1);
      setUploadSuccess(true);
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const columns = inspectResult?.columns ?? [];
  const sheets = inspectResult?.sheets ?? null;

  return (
    <div className="border border-gray-200 rounded-md p-3 space-y-3">
      {/* Header: site name + existing summary + collapse toggle */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-800">{siteName}</p>
        <div className="flex items-center gap-2">
          {uploadedSummary?.hasData && (
            <span className="text-xs text-green-700 whitespace-nowrap">
              {uploadedSummary.rowCount?.toLocaleString()} rows &middot;{' '}
              {uploadedSummary.startDate?.slice(0, 10)} &rarr; {uploadedSummary.endDate?.slice(0, 10)}
            </span>
          )}
          {inspectResult && (
            <button
              type="button"
              className="text-xs text-gray-400 hover:text-gray-600 leading-none"
              onClick={() => setCollapsed((v) => !v)}
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              {collapsed ? '▼' : '▲'}
            </button>
          )}
        </div>
      </div>

      {/* File picker */}
      <div className="flex items-center gap-2">
        <input
          key={clearKey}
          type="file"
          accept=".csv,.xlsx"
          className="text-sm"
          onChange={(e) => handleFileSelect(e.target.files?.[0] ?? null)}
        />
        {file && (
          <button
            type="button"
            className="text-xs text-gray-400 hover:text-red-600"
            onClick={() => {
              setFile(null);
              setInspectResult(null);
              setInspectError(null);
              setClearKey((k) => k + 1);
            }}
          >
            Clear
          </button>
        )}
      </div>

      {isInspecting && <p className="text-xs text-gray-500">Reading file…</p>}
      {inspectError && <p className="text-xs text-red-600">{inspectError}</p>}

      {/* Config form — appears after inspect, stays visible after upload */}
      {inspectResult && !collapsed && (
        <div className="space-y-2 pt-1 border-t border-gray-100">
          {/* Sheet selector (XLSX with >1 sheet) */}
          {isXlsx && sheets && sheets.length > 1 && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 w-40 shrink-0">Sheet</label>
              <select
                className="text-sm border-gray-300 rounded px-1 py-0.5 flex-1"
                value={sheetName || sheets[0]}
                onChange={(e) => handleSheetChange(e.target.value)}
              >
                {sheets.map((s, i) => (
                  <option key={s} value={s}>
                    {s}{i === 0 ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Datetime column */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 w-40 shrink-0">
              Datetime column <span className="text-red-500">*</span>
            </label>
            <select
              className="text-sm border-gray-300 rounded px-1 py-0.5 flex-1"
              value={datetimeCol}
              onChange={(e) => setDatetimeCol(e.target.value)}
            >
              <option value="">— select —</option>
              {columns.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Observation column */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 w-40 shrink-0">
              Observation column <span className="text-red-500">*</span>
            </label>
            <select
              className="text-sm border-gray-300 rounded px-1 py-0.5 flex-1"
              value={obsCol}
              onChange={(e) => setObsCol(e.target.value)}
            >
              <option value="">— select —</option>
              {columns.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Timezone */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 w-40 shrink-0">
              Timezone <span className="text-red-500">*</span>
            </label>
            <input
              list={TZ_DATALIST_ID}
              className="text-sm border border-gray-300 rounded px-1 py-0.5 flex-1"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              placeholder="e.g. America/Boise"
            />
          </div>

          {/* Data frequency */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 w-40 shrink-0">Data frequency</label>
            <select
              className="text-sm border-gray-300 rounded px-1 py-0.5 flex-1"
              value={dataFrequency}
              onChange={(e) => setDataFrequency(e.target.value)}
            >
              {OBS_DATA_FREQUENCY_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          {/* Data units */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 w-40 shrink-0">Data units</label>
            <select
              className="text-sm border-gray-300 rounded px-1 py-0.5 flex-1"
              value={dataUnits}
              onChange={(e) => setDataUnits(e.target.value)}
            >
              {OBS_DATA_UNITS_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          {/* Sample timing */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 w-40 shrink-0">Sample timing</label>
            <select
              className="text-sm border-gray-300 rounded px-1 py-0.5 flex-1"
              value={sampleTiming}
              onChange={(e) => setSampleTiming(e.target.value)}
            >
              {OBS_SAMPLE_TIMING_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          {/* Type of flow */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 w-40 shrink-0">Type of flow</label>
            <select
              className="text-sm border-gray-300 rounded px-1 py-0.5 flex-1"
              value={typeOfFlow}
              onChange={(e) => setTypeOfFlow(e.target.value)}
            >
              {OBS_FLOW_TYPE_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          {/* Data source type */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 w-40 shrink-0">Data source type</label>
            <select
              className="text-sm border-gray-300 rounded px-1 py-0.5 flex-1"
              value={dataSourceType}
              onChange={(e) => setDataSourceType(e.target.value)}
            >
              {OBS_DATA_SOURCE_TYPE_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          {/* Missing data value */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 w-40 shrink-0">Missing data value</label>
            <input
              type="text"
              className="text-sm border border-gray-300 rounded px-1 py-0.5 w-24"
              value={missingDataValue}
              onChange={(e) => setMissingDataValue(e.target.value)}
            />
          </div>

          {/* Advanced options */}
          <div>
            <button
              type="button"
              className="text-xs text-blue-600 hover:underline"
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? '▲ Hide advanced' : '▼ Advanced'}
            </button>
            {showAdvanced && (
              <div className="mt-2 space-y-2 pl-3 border-l-2 border-gray-100">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-600 w-40 shrink-0">
                    Header row (0-indexed)
                  </label>
                  <input
                    type="number"
                    min={0}
                    className="text-sm border border-gray-300 rounded px-1 py-0.5 w-16"
                    value={headerRow}
                    onChange={(e) => handleHeaderChange(e.target.value)}
                  />
                </div>
                {!isXlsx && (
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-600 w-40 shrink-0">
                      Day-first dates (CSV)
                    </label>
                    <input
                      type="checkbox"
                      checked={dayfirst}
                      onChange={(e) => setDayfirst(e.target.checked)}
                      className="mr-1"
                    />
                    <span className="text-xs text-gray-500">
                      01/02/2020 → Feb 1 (not Jan 2)
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Preview */}
          {inspectResult.head.length > 0 && (
            <div>
              <button
                type="button"
                className="text-xs text-blue-600 hover:underline"
                onClick={() => setShowPreview((v) => !v)}
              >
                {showPreview ? '▲ Hide preview' : '▼ Show preview'}
              </button>
              {showPreview && (
                <div className="mt-2 overflow-x-auto">
                  <table className="text-xs border-collapse">
                    <thead>
                      <tr>
                        {Object.keys(inspectResult.head[0]).map((col) => (
                          <th
                            key={col}
                            className="border border-gray-300 bg-gray-50 px-2 py-0.5 text-left font-medium text-gray-600 whitespace-nowrap"
                          >
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {inspectResult.head.map((row, i) => (
                        <tr key={i} className="odd:bg-white even:bg-gray-50">
                          {Object.values(row).map((val, j) => (
                            <td
                              key={j}
                              className="border border-gray-200 px-2 py-0.5 text-gray-700 whitespace-nowrap"
                            >
                              {val}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Upload button / success state */}
          <div className="flex items-center gap-2 pt-1">
            {file ? (
              <button
                type="button"
                disabled={!canUpload}
                className={buttonPrimary}
                onClick={handleUpload}
              >
                {isUploading ? 'Uploading…' : 'Upload'}
              </button>
            ) : uploadSuccess ? (
              <span className="text-xs text-green-700">Uploaded. Select a new file to re-upload.</span>
            ) : null}
            {uploadError && <p className="text-xs text-red-600 max-w-prose">{uploadError}</p>}
          </div>
        </div>
      )}

      {/* Chart + cleanup params */}
      {isPreviewLoading && <p className="text-xs text-gray-500">Generating preview…</p>}
      {previewError && (
        <p className="text-xs text-amber-600 max-w-prose">Preview: {previewError}</p>
      )}
      {chartData && !isPreviewLoading && (
        <div className="pt-2 space-y-2 border-t border-gray-100">
          <ObsChart obsData={chartData} cleanupParams={liveCleanupParams} />
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Mask above</label>
              <input
                type="number"
                className="w-24 text-sm border-gray-300 rounded px-1 py-0.5"
                value={maskAbove}
                onChange={(e) => setMaskAbove(e.target.value)}
                placeholder="none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Mask below</label>
              <input
                type="number"
                className="w-24 text-sm border-gray-300 rounded px-1 py-0.5"
                value={maskBelow}
                onChange={(e) => setMaskBelow(e.target.value)}
                placeholder="none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Gaussian smooth σ</label>
              <input
                type="number"
                min={0}
                step={0.5}
                className="w-20 text-sm border-gray-300 rounded px-1 py-0.5"
                value={gaussianSigma}
                onChange={(e) => setGaussianSigma(e.target.value)}
              />
            </div>
            <button
              type="button"
              disabled={isSavingCleanup || isMutating || !hasCleanupChanges}
              className={buttonPrimary}
              onClick={handleSaveCleanup}
            >
              {isSavingCleanup ? 'Saving…' : 'Save cleanup'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Main section ---

export default function ObservationsSection({
  orgId,
  projectId,
  payload,
  queryKey,
  stepErrors,
  prerequisiteWarning,
}: SetupSectionProps) {
  const view = useMemo(() => getStateViewDefaults(payload?.data), [payload?.data]);
  const queryClient = useQueryClient();
  const {
    mutate,
    mutatingAction,
    isMutating,
    error: mutationError,
    lastCliCommand,
  } = useSetupMutation(queryKey);

  const [fetchPublicResult, setFetchPublicResult] = React.useState<{
    fetched: string[];
    errors: string[];
  } | null>(null);

  const handleFetchPublic = async () => {
    setFetchPublicResult(null);
    const result = await mutate('fetch-public', () =>
      setupWizardApi.fetchPublicObservations(orgId, projectId)
    );
    if (result) {
      setFetchPublicResult({fetched: result.fetchedSites, errors: result.fetchErrors});
      result.fetchedSites.forEach((site) =>
        queryClient.invalidateQueries(makeSiteObsQueryKey(orgId, projectId, site))
      );
    }
  };

  const handleSaveCleanupParams = async (site: string, params: SiteCleanupParams) => {
    const newInputValues = {
      ...view.siteInputValues,
      [site]: {...(view.siteInputValues[site] ?? {}), ...params},
    };
    await mutate('save-cleanup', () =>
      setupWizardApi.updateCleanupParams(orgId, projectId, newInputValues)
    );
  };

  const privateSiteNames = view.obsProcessing
    .map((r) => String(r[SITE_NAME_COLUMN] ?? ''))
    .filter(Boolean);

  const sitesWithPublicGauges = view.siteInfo.filter(
    (r) => r[GAUGE_ID_COLUMN] && r[GAUGE_TYPE_COLUMN] !== 'Private'
  );

  return (
    <div className="space-y-8">
      {prerequisiteWarning && <PrerequisiteWarning message={prerequisiteWarning} />}
      <SectionErrors errors={stepErrors} />
      <ApiErrorMessage message={mutationError} />
      <CliCommandBlock command={lastCliCommand} />

      {/* Shared timezone datalist for all SiteObsUploader instances */}
      <datalist id={TZ_DATALIST_ID}>
        {COMMON_TIMEZONES.map((tz) => (
          <option key={tz} value={tz} />
        ))}
      </datalist>

      {/* Upload Observations */}
      {privateSiteNames.length > 0 ? (
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">Upload Observations</h3>
          <p className="text-xs text-gray-500 mb-3">
            Select a CSV or XLSX file for each site. Column names, sheet, and other options appear
            after the file is read.
          </p>
          <div className="space-y-3">
            {privateSiteNames.map((site) => (
              <SiteObsUploader
                key={site}
                orgId={orgId}
                projectId={projectId}
                siteName={site}
                uploadedSummary={view.uploadedObsSummary.find((s) => s.siteName === site)}
                queryKey={queryKey}
                cleanupParams={view.siteInputValues[site] ?? {}}
                onSaveCleanup={(params) => handleSaveCleanupParams(site, params)}
                isMutating={isMutating}
              />
            ))}
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          No sites with private observations. Set &quot;{ARE_OBSERVATIONS_AVAILABLE_COLUMN}&quot;
          and &quot;{GAUGE_TYPE_COLUMN}&quot; to &quot;Private&quot; in the Define Sites step first.
        </p>
      )}

      {/* Fetch Public Observations */}
      {sitesWithPublicGauges.length > 0 && (
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-medium text-gray-900 mb-2">
            Fetch Public Gauge Observations
          </h3>
          <p className="text-xs text-gray-500 mb-3">
            {sitesWithPublicGauges.length} site(s) have a Remote Gauge ID with a public data source.
            Click below to fetch their observations from our database.
          </p>
          <div className="mb-3 space-y-2">
            {sitesWithPublicGauges.map((site) => {
              const siteName = String(site[SITE_NAME_COLUMN] ?? '');
              const summary = view.uploadedObsSummary.find((s) => s.siteName === siteName);
              const hasFetchedData = summary?.hasData ?? false;
              return (
                <div key={siteName} className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-gray-800">{siteName}</span>
                    <span className="text-xs text-gray-500">
                      ({String(site[GAUGE_TYPE_COLUMN] ?? '')} {String(site[GAUGE_ID_COLUMN] ?? '')}
                      )
                    </span>
                    {hasFetchedData ? (
                      <span className="text-xs text-green-700">
                        {summary?.rowCount?.toLocaleString()} rows &middot;{' '}
                        {summary?.startDate?.slice(0, 10)} &rarr; {summary?.endDate?.slice(0, 10)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">not yet fetched</span>
                    )}
                  </div>
                  {hasFetchedData && (
                    <GaugeDataChart orgId={orgId} projectId={projectId} siteName={siteName} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={isMutating}
              className={buttonPrimary}
              onClick={handleFetchPublic}
            >
              {mutatingAction === 'fetch-public' ? 'Fetching…' : 'Fetch Public Observations'}
            </button>
          </div>
          {fetchPublicResult && (
            <div className="mt-2 space-y-1">
              {fetchPublicResult.fetched.length > 0 && (
                <p className="text-sm text-green-700">
                  Fetched: {fetchPublicResult.fetched.join(', ')}
                </p>
              )}
              {fetchPublicResult.errors.map((err, i) => {
                const urlMatch = err.match(/(https?:\/\/\S+)$/);
                if (urlMatch) {
                  const text = err.slice(0, urlMatch.index).trimEnd();
                  return (
                    <p key={i} className="text-sm text-red-600">
                      {text}{' '}
                      <a href={urlMatch[1]} target="_blank" rel="noreferrer" className="underline">
                        View data here
                      </a>
                    </p>
                  );
                }
                return (
                  <p key={i} className="text-sm text-red-600">
                    {err}
                  </p>
                );
              })}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
